import { useSelector } from 'react-redux';
import { selectors as orderSelectors } from 'state/order';
import { selectors as settingsSelectors } from 'state/settings';
import useSWR from 'swr';
import api from 'api/api';
import { createContext, useContext, useMemo, useState } from 'react';
import { validators as loadValidators } from 'utils/loads';

const suitableVehicleTypeContext = createContext(null);
suitableVehicleTypeContext.displayName = 'suitableVehicleTypeContext';

const getValidLoads = ({
  shipmentsForQuote,
  settings,
  validations,
  orderType,
}) => {
  let validLoads = [];
  Object.keys(shipmentsForQuote).forEach((key) => {
    const shipmentLoads = shipmentsForQuote[key].loads;
    const shipmentValidLoads = shipmentLoads.filter((load) => {
      const { errors } = loadValidators.validateLoad({
        load,
        settings,
        validations,
        orderType,
        onlyDirty: false,
      });
      const numberOfErrors = Object.keys(errors).length;
      const isLoadValid = numberOfErrors === 0;
      return isLoadValid;
    });
    validLoads.push(...shipmentValidLoads);
  });
  return validLoads;
};

export const SuitableVehicleTypeContextProvider = ({ children }) => {
  const [vehicleType, setVehicleType] = useState(null);
  const contextValue = {
    vehicleType,
    setVehicleType,
  };
  return (
    <suitableVehicleTypeContext.Provider value={contextValue}>
      {children}
    </suitableVehicleTypeContext.Provider>
  );
};

const useSuitableVehicleType = () => {
  const { vehicleType, setVehicleType } = useContext(
    suitableVehicleTypeContext
  );

  const shipmentsForQuote = useSelector(orderSelectors.getShipmentsForQuote);
  const extras = useSelector(orderSelectors.getExtras);
  const orderType = useSelector(orderSelectors.getTypeOfOrder);
  const settings = useSelector(settingsSelectors.getCountrySettings);
  const validations = useSelector(orderSelectors.getValidations);

  const validLoads = useMemo(
    () =>
      getValidLoads({
        shipmentsForQuote,
        settings,
        validations,
        orderType,
      }),
    [shipmentsForQuote, orderType, settings, validations]
  );

  const hasEnoughDataToRequestSuitableVehicleType = useMemo(
    () => validLoads.length > 0,
    [validLoads]
  );

  const requestBody = useMemo(() => {
    const body = {
      loads: validLoads,
      has_liftgate: extras.includes('LIFTGATE'),
      has_side_load: extras.includes('SIDE_LOAD'),
      has_top_load: extras.includes('TOP_LOAD'),
    };
    return body;
  }, [validLoads, extras]);

  const requestKey = useMemo(() => JSON.stringify(requestBody), [requestBody]);

  if (!hasEnoughDataToRequestSuitableVehicleType) {
    setVehicleType(null);
  }

  const fetcher = async () => {
    return api.getSuitableVehicleType({ params: requestBody });
  };

  const { data, error } = useSWR(
    hasEnoughDataToRequestSuitableVehicleType ? [requestKey] : null,
    fetcher,
    {
      shouldRetryOnError: false,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const isRequestFinished = useMemo(
    () => !error && data !== undefined,
    [error, data]
  );

  const newVehicleType = isRequestFinished ? data.data.vehicle_type : null;

  if (isRequestFinished && vehicleType !== newVehicleType) {
    setVehicleType(newVehicleType);
  }

  return vehicleType;
};

export default useSuitableVehicleType;
